<script setup lang="ts">
import LeButton from "@dsk/components/Button/LeButton.vue";
import LeIconButton from "@dsk/components/IconButton/LeIconButton.vue";
import LeTooltip from "@dsk/components/LeTooltip.vue";
import LeChevronUp from "@dsk/icons/LeChevronUp.vue";
import LeFeedbackUpDown from "@dsk/icons/LeFeedbackUpDown.vue";
import LeMagicGrey from "@dsk/icons/LeMagicGrey.vue";
import { computed, nextTick, ref } from "vue";

import LeFeedbackHighlight from "@/components/feedback-modal/LeHighlightRatingModal.vue";
import { config } from "@/config";
import { ArticleFeedbackService } from "@/services";
import type { DictsysArticle, DictsysExample } from "@/types";

const props = defineProps<{
  usageLanguage: DictsysArticle["usageLanguage"];
  sourceLanguage: string;
  targetLanguage: string;
  examples: DictsysExample[];
}>();

const { isInternalSite, isProduction } = config.app;

const isOpen = ref<boolean>(false);

const language = computed<"da" | "de">(() => (props.usageLanguage === "de" ? "de" : "da"));
const dictoTooltip = computed<string>(() => (language.value === "de" ? "Inhalt von Dicto" : "Indhold fra Dicto"));
const dictoRatingTooltip = computed<string>(() =>
  language.value === "de" ? "Diesen Inhalt bewerten" : "Vurder dette indhold"
);
const expandText = computed<string>(() =>
  language.value === "de" ? "Weitere Beispiele anzeigen" : "Se flere eksempler"
);
const collapseText = computed<string>(() =>
  language.value === "de" ? "Weitere Beispiele verbergen" : "Skjul eksempler"
);

const expandCollapseText = computed(() => {
  if (isOpen.value) {
    return collapseText.value;
  }

  return expandText.value;
});

const filteredExamples = computed<DictsysExample[]>(() => {
  if (isInternalSite || !isProduction) {
    return props.examples;
  }

  return props.examples
    .filter((example) => !(example.isExampleGenerated && example.isTranslationGenerated))
    .map((example) => {
      if (example.isExampleGenerated) {
        return { ...example, example: "", isExampleGenerated: false };
      }

      if (example.isTranslationGenerated) {
        return { ...example, translation: "", isTranslationGenerated: false };
      }

      return example;
    });
});

const EXPAND_THRESHOLD = 2;
const isExpandable = computed<boolean>(() => filteredExamples.value.length > EXPAND_THRESHOLD);
const displayedExamples = computed<DictsysExample[]>(() =>
  isOpen.value ? filteredExamples.value : filteredExamples.value.slice(0, EXPAND_THRESHOLD)
);

const showDictoIndicator = ref<boolean>(true);
const ratingModalExampleId = ref<number | null>(null);
const ratingModalExampleType = ref<"example" | "translation" | "both" | null>(null);

const openRatingModal = async (example: DictsysExample) => {
  // Hide the dicto elements and wait for the DOM, so the modal doesn't highlight those elements
  showDictoIndicator.value = false;
  await nextTick();

  if (example.isExampleGenerated && example.isTranslationGenerated) {
    ratingModalExampleType.value = "both";
  } else if (example.isExampleGenerated) {
    ratingModalExampleType.value = "example";
  } else if (example.isTranslationGenerated) {
    ratingModalExampleType.value = "translation";
  }

  ratingModalExampleId.value = example.id;
};

const closeFeedback = () => {
  showDictoIndicator.value = true;
  ratingModalExampleId.value = null;
  ratingModalExampleType.value = null;
};

const onRate = (rating: boolean, exampleId: number) => {
  ArticleFeedbackService.rateDictsysExample(exampleId, rating);
};
</script>

<template>
  <div v-if="displayedExamples.length > 0">
    <div v-for="(example, exampleIndex) in displayedExamples" :key="exampleIndex">
      <LeFeedbackHighlight
        :padding="10"
        :is-open="ratingModalExampleType === 'both' && ratingModalExampleId === example.id"
        class="le-article-translation-examples__example-feedback-container"
        @rate="onRate($event, example.id)"
        @close="closeFeedback()"
      >
        <div
          class="le-article-translation-examples__example-wrapper"
          :class="{
            'le-article-translation-examples__example-wrapper--last': exampleIndex === displayedExamples.length - 1
          }"
        >
          <span
            v-if="example.example"
            class="le-article-translation-examples__example-text"
            :class="{ 'le-article-translation-examples__example-text--not-last': example.translation }"
            :lang="props.targetLanguage"
          >
            <LeFeedbackHighlight
              tag="span"
              :padding="10"
              :is-open="ratingModalExampleType === 'example' && ratingModalExampleId === example.id"
              @rate="onRate($event, example.id)"
              @close="closeFeedback()"
            >
              {{ example.example }}
            </LeFeedbackHighlight>
            <template v-if="showDictoIndicator && (example.isExampleGenerated || example.isTranslationGenerated)">
              <LeTooltip
                class="le-article-translation-examples__feedback-icon"
                :margin-bottom="3"
                :text="dictoRatingTooltip"
              >
                <LeIconButton @click="openRatingModal(example)">
                  <LeFeedbackUpDown role="presentation" />
                </LeIconButton>
              </LeTooltip>

              <LeTooltip class="le-article-translation-examples__magic-icon" :margin-bottom="8" :text="dictoTooltip">
                <LeMagicGrey role="presentation" />
              </LeTooltip>
            </template>
          </span>
          <i v-if="example.translation" :lang="props.sourceLanguage">
            <LeFeedbackHighlight
              tag="span"
              :padding="10"
              :is-open="ratingModalExampleType === 'translation' && ratingModalExampleId === example.id"
              @rate="onRate($event, example.id)"
              @close="closeFeedback()"
            >
              {{ example.translation }}
            </LeFeedbackHighlight>
          </i>
        </div>
      </LeFeedbackHighlight>
    </div>

    <LeButton
      v-if="isExpandable"
      variant="outline"
      class="le-article-translation-examples__button"
      @click="isOpen = !isOpen"
      >{{ expandCollapseText }}
      <LeChevronUp
        :class="{ 'le-article-translation-examples__button-icon--active': isOpen }"
        class="le-article-translation-examples__button-icon"
    /></LeButton>
  </div>
</template>

<style lang="less" scoped>
@import "@dsk/styles/all";
@import "@/assets/styles/dictsys-article";

.le-article-translation-examples {
  &__example-feedback-container {
    display: inline-flex;
  }

  &__example-wrapper {
    display: inline-flex;
    flex-direction: column;
    margin-bottom: 8px;
    color: @colors[@neutrals][500];
    .dictsys-article-text();

    &--last {
      margin-bottom: 0;
    }
  }

  &__example-text {
    display: flex;
    align-items: center;

    &--not-last {
      margin-bottom: 2px;
    }
  }

  &__button {
    margin-top: 18px;
  }

  &__button-icon {
    transform: rotate(180deg);

    &--active {
      transform: none;
    }
  }

  &__feedback-icon {
    top: -6px;
    height: 16px;
    margin-left: 8px;
  }

  &__magic-icon {
    height: 16px;
    margin-left: 10px;
  }
}
</style>
